import { useRef, useState, useCallback } from "react";

// pdfjs-dist types use private identifiers incompatible with es5 target,
// so we use require + any typing
// eslint-disable-next-line @typescript-eslint/no-var-requires
const pdfjs: any = require("pdfjs-dist");

// Use CDN for the worker — must match installed pdfjs-dist version (3.11.174)
pdfjs.GlobalWorkerOptions.workerSrc = "https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js";

export function usePdfViewer() {
  const pdfCanvasRef = useRef<HTMLCanvasElement>(null);
  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScaleState] = useState(1);
  const [rotation, setRotationState] = useState(0);
  const [rendering, setRendering] = useState(false);
  const renderTaskRef = useRef<any>(null);

  const loadDocument = useCallback(async (url: string) => {
    try {
      const doc = await pdfjs.getDocument(url).promise;
      setPdfDoc(doc);
      setTotalPages(doc.numPages);
      setCurrentPage(1);
      return doc;
    } catch (err) {
      console.error("Failed to load PDF:", err);
      return null;
    }
  }, []);

  const renderPage = useCallback(
    async (
      pageNum: number,
      scaleVal?: number,
      rotationVal?: number,
      doc?: any,
    ) => {
      const docToUse = doc || pdfDoc;
      if (!docToUse || !pdfCanvasRef.current) {
        console.warn("[PDF] renderPage bail: doc=", !!docToUse, "canvas=", !!pdfCanvasRef.current);
        return { width: 0, height: 0 };
      }

      const actualScale = scaleVal ?? scale;
      const actualRotation = rotationVal ?? rotation;

      // Cancel any in-progress render to prevent "Cannot use the same canvas
      // during multiple render() operations" error
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch {}
        renderTaskRef.current = null;
      }

      setRendering(true);
      try {
        // Cancel any in-flight render to avoid "canvas in use" error
        if (renderTaskRef.current) {
          renderTaskRef.current.cancel();
          renderTaskRef.current = null;
        }

        const page = await docToUse.getPage(pageNum);
        const viewport = page.getViewport({
          scale: actualScale,
          rotation: actualRotation,
        });

        const canvas = pdfCanvasRef.current;
        const ctx = canvas.getContext("2d")!;
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        const task = page.render({ canvasContext: ctx, viewport });
        renderTaskRef.current = task;
        await task.promise;
        renderTaskRef.current = null;
        setCurrentPage(pageNum);
        console.log("[PDF] renderPage success: page=", pageNum, "dims=", viewport.width, "x", viewport.height);
        return { width: viewport.width, height: viewport.height };
      } catch (err: any) {
        // Ignore cancellation errors (expected when we cancel a previous render)
        if (err?.name === "RenderingCancelledException") {
          return { width: 0, height: 0 };
        }
        console.error("Failed to render page:", err);
        return { width: 0, height: 0 };
      } finally {
        setRendering(false);
      }
    },
    [pdfDoc, scale, rotation],
  );

  const goToPage = useCallback(
    async (pageNum: number) => {
      if (pageNum < 1 || pageNum > totalPages) return;
      await renderPage(pageNum);
    },
    [totalPages, renderPage],
  );

  const setScale = useCallback(
    async (newScale: number) => {
      setScaleState(newScale);
      return renderPage(currentPage, newScale, rotation);
    },
    [currentPage, rotation, renderPage],
  );

  const setRotation = useCallback(
    async (newRotation: number) => {
      setRotationState(newRotation);
      return renderPage(currentPage, scale, newRotation);
    },
    [currentPage, scale, renderPage],
  );

  const cancelRender = useCallback(() => {
    if (renderTaskRef.current) {
      renderTaskRef.current.cancel();
      renderTaskRef.current = null;
    }
  }, []);

  return {
    pdfCanvasRef,
    totalPages,
    currentPage,
    scale,
    rotation,
    rendering,
    loadDocument,
    renderPage,
    goToPage,
    setScale,
    setRotation,
    setScaleState,
    setRotationState,
    cancelRender,
  };
}
